import {useCallback, useEffect, useReducer} from 'react';

import usePrevious from 'ui-library/hooks/usePrevious';
import {assertNever, ElementMouseEvent} from 'util/Types';

import useDnDMouseMoveHandler, {MouseDelta} from './useDnDMouseMoveHandler';
import useDnDScrollHandler, {ScrollDelta} from './useDnDScrollHandler';

export type HoverPosition = 'left' | 'right';

type HoverState<T> = {isHovering: false} | {isHovering: true; hoveredItem: T; hoverPosition: HoverPosition};

interface DragDelta {
  translateX: number;
  translateY: number;
}

export interface NotDraggingDragState {
  isDragging: false;
}

export type IsDraggingDragState<T> = {
  isDragging: true;
  draggedItem: T;
  dragDelta: DragDelta;
} & HoverState<T>;

export type DragState<T> = NotDraggingDragState | IsDraggingDragState<T>;

export enum DragStateActionType {
  ITEM_HOVER_ENTER = 'ITEM_HOVER_ENTER',
  ITEM_HOVER_LEAVE = 'ITEM_HOVER_LEAVE',
  MOVE_MOUSE = 'MOVE_MOUSE',
  SCROLL = 'SCROLL',
  START_DRAGGING = 'START_DRAGGING',
  STOP_DRAGGING = 'STOP_DRAGGING',
}

export type DragStateAction<T> =
  | {type: DragStateActionType.ITEM_HOVER_ENTER; itemToHover: T; hoverPosition: HoverPosition}
  | {type: DragStateActionType.ITEM_HOVER_LEAVE}
  | {type: DragStateActionType.MOVE_MOUSE; mouseDelta: MouseDelta}
  | {type: DragStateActionType.SCROLL; scrollDelta: ScrollDelta}
  | {type: DragStateActionType.START_DRAGGING; itemToDrag: T}
  | {type: DragStateActionType.STOP_DRAGGING};

export function createDragStateReducer<T>(): (
  dragState: DragState<T>,
  action: DragStateAction<T>
) => DragState<T> {
  return (dragState: DragState<T>, action: DragStateAction<T>) => {
    if (dragState.isDragging) {
      switch (action.type) {
        case DragStateActionType.ITEM_HOVER_ENTER:
          if (dragState.draggedItem === action.itemToHover) {
            return dragState;
          }
          if (
            dragState.isHovering &&
            dragState.hoveredItem === action.itemToHover &&
            dragState.hoverPosition === action.hoverPosition
          ) {
            return dragState;
          }

          return {
            ...dragState,
            isHovering: true,
            hoveredItem: action.itemToHover,
            hoverPosition: action.hoverPosition,
          };
        case DragStateActionType.ITEM_HOVER_LEAVE:
          if (dragState.isHovering) {
            return {
              isDragging: true,
              draggedItem: dragState.draggedItem,
              dragDelta: dragState.dragDelta,
              isHovering: false,
            };
          }
          return dragState;
        case DragStateActionType.MOVE_MOUSE:
          return {
            ...dragState,
            dragDelta: {
              translateX: dragState.dragDelta.translateX + action.mouseDelta.deltaX,
              translateY: dragState.dragDelta.translateY + action.mouseDelta.deltaY,
            },
          };
        case DragStateActionType.SCROLL:
          return {
            ...dragState,
            dragDelta: {
              translateX: dragState.dragDelta.translateX + action.scrollDelta.deltaLeft,
              translateY: dragState.dragDelta.translateY + action.scrollDelta.deltaTop,
            },
          };
        case DragStateActionType.START_DRAGGING:
          return dragState;
        case DragStateActionType.STOP_DRAGGING:
          return {isDragging: false};
        default:
          throw new Error(`Invalid action type ${assertNever(action)}`);
      }
    } else {
      switch (action.type) {
        case DragStateActionType.ITEM_HOVER_ENTER:
        case DragStateActionType.ITEM_HOVER_LEAVE:
        case DragStateActionType.MOVE_MOUSE:
        case DragStateActionType.SCROLL:
        case DragStateActionType.STOP_DRAGGING:
          return dragState;
        case DragStateActionType.START_DRAGGING:
          return {
            isDragging: true,
            draggedItem: action.itemToDrag,
            dragDelta: {
              translateX: 0,
              translateY: 0,
            },
            isHovering: false,
          };
        default:
          throw new Error(`Invalid action type ${assertNever(action)}`);
      }
    }
  };
}

export interface UseDnDCanvasDragStateInput<T> {
  onMoveItem: (sourceItem: T, targetItem: T, hoverPosition?: HoverPosition) => void;
}

export interface UseDnDCanvasDragStateOutput<T> {
  dragState: DragState<T>;
  onItemDragStart: (e: ElementMouseEvent, itemToDrag: T) => void;
  onItemHoverEnter: (itemToHover: T, hoverPosition: HoverPosition) => void;
  onItemHoverLeave: () => void;
  // _handleOnDragEnd is only exported to be tested, and should not be used by callers
  _handleOnDragEnd: () => void;
}

/**
 * Hook that manages the drag state used by DnDCanvas.
 *
 * Word on how mouse vs scroll events work while dragging objects:
 * - onMouseMove: Updates of the mouse location are meant to work with event.pageX/Y, which should keep the blocks
 *   in the right place while scrolling. However, those event.pageX/Y only work if we scroll the whole document,
 *   so if our component is in a div, the page doesn't move. Instead we build our own "pageX/Y" (called drag delta)
 *   by looking at the clientX/Y and applying the difference between the current and previous mouse clientX/Y to
 *   the dragDelta.
 * - onScroll: Unfortunately, scroll events do not come with updated cursor coordinates, but since we're
 *   scrolling the mouse is moving relative to the amount that the page/element scrolls. Instead we get "scrollTop"
 *   and "scrollLeft" amounts, which define how far from the top/left the user has scrolled. We use these to
 *   "update" dragDelta to make it follow the cursor while scrolling. So instead of making the block
 *   location = coordinates, we simply apply the scroll vector onto it. We update the scroll in a similar fashion
 *   as the mouse position where we generate delta vectors between the current and last scroll position and
 *   apply that to the dragDelta.
 *
 * @param onMoveItem function that handles moving items in the DnD layout to make
 */
export default function useDnDCanvasDragState<T>({
  onMoveItem,
}: UseDnDCanvasDragStateInput<T>): UseDnDCanvasDragStateOutput<T> {
  // Inspired by https://stackoverflow.com/questions/55396438/generic-type-in-usereducer-for-a-returned-parameter
  // We can't directly specify the generic of a reducer, so instead we make a function that takes the generic
  // and returns the reducer with the proper type. Simply defining dragStateReducer without the "createReducer"
  // function makes dragState be of type DragState<unknown> and not DragState<T>
  const dragStateReducer = createDragStateReducer<T>();
  const [dragState, dispatchDragState] = useReducer(dragStateReducer, {isDragging: false});
  const previousDragState = usePrevious<DragState<T>>(dragState);

  const setMouseDelta = useCallback((mouseDelta: MouseDelta) => {
    dispatchDragState({
      type: DragStateActionType.MOVE_MOUSE,
      mouseDelta,
    });
  }, []);

  const setScrollDelta = useCallback((scrollDelta: ScrollDelta) => {
    dispatchDragState({
      type: DragStateActionType.SCROLL,
      scrollDelta,
    });
  }, []);

  const {handleMouseMove, setMousePosition} = useDnDMouseMoveHandler({setMouseDelta});
  const {handleScroll} = useDnDScrollHandler({setScrollDelta});

  const handleOnDragStart = useCallback(
    (e: ElementMouseEvent, itemToDrag: T) => {
      dispatchDragState({
        type: DragStateActionType.START_DRAGGING,
        itemToDrag,
      });

      setMousePosition(e);
    },
    [dispatchDragState, setMousePosition]
  );

  const handleOnDragEnd = useCallback(() => {
    dispatchDragState({
      type: DragStateActionType.STOP_DRAGGING,
    });
  }, []);

  const removeEventListeners = useCallback(() => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleOnDragEnd);
    window.removeEventListener('scroll', handleScroll);
  }, [handleMouseMove, handleOnDragEnd, handleScroll]);

  const onStartDragging = useCallback(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleOnDragEnd);
    // "true" turns on capture mode, which allows the event to be caught during the capture phase (on the way
    // down from parent to child); otherwise, the event doesn't get triggered because the child components might
    // prevent the event from bubbling (going from child to parent) during the bubbling phase.
    // See https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener for more details.
    window.addEventListener('scroll', handleScroll, true);
  }, [handleMouseMove, handleOnDragEnd, handleScroll]);

  const {isDragging} = dragState;
  const previousIsDragging = previousDragState ? previousDragState.isDragging : false;

  const previousDraggedItem = previousDragState?.isDragging ? previousDragState.draggedItem : undefined;

  const previousHoveredItem =
    previousDragState?.isDragging && previousDragState.isHovering ? previousDragState.hoveredItem : undefined;

  const previousHoverPosition =
    previousDragState?.isDragging && previousDragState.isHovering
      ? previousDragState.hoverPosition
      : undefined;

  const onStopDragging = useCallback(() => {
    if (previousDraggedItem && previousHoveredItem) {
      onMoveItem(previousDraggedItem, previousHoveredItem, previousHoverPosition);
    }

    removeEventListeners();
  }, [removeEventListeners, onMoveItem, previousDraggedItem, previousHoveredItem, previousHoverPosition]);

  useEffect(() => {
    if (isDragging && !previousIsDragging) {
      onStartDragging();
    }

    if (!isDragging && previousIsDragging) {
      onStopDragging();
    }
  }, [isDragging, previousIsDragging, onStartDragging, onStopDragging]);

  useEffect(
    () => () => {
      removeEventListeners();
    },
    [removeEventListeners]
  );

  const handleItemHoverEnter = useCallback((itemToHover: T, hoverPosition: HoverPosition) => {
    dispatchDragState({
      type: DragStateActionType.ITEM_HOVER_ENTER,
      itemToHover,
      hoverPosition,
    });
  }, []);

  const handleItemHoverLeave = useCallback(() => {
    dispatchDragState({
      type: DragStateActionType.ITEM_HOVER_LEAVE,
    });
  }, []);

  return {
    dragState,
    onItemDragStart: handleOnDragStart,
    onItemHoverEnter: handleItemHoverEnter,
    onItemHoverLeave: handleItemHoverLeave,
    _handleOnDragEnd: handleOnDragEnd,
  };
}
